import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, DollarSign, User, Clock, CheckCircle2, Circle, MessageSquare, MessageCircle } from 'lucide-react';
import { Deal, DealTask, useDeals } from '@/hooks/useDeals';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { GanttChart } from './GanttChart';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { EnhancedDealMessaging } from './EnhancedDealMessaging';
interface DealDetailModalProps {
  deal: Deal | null;
  isOpen: boolean;
  onClose: () => void;
  progress: number;
}

export const DealDetailModal: React.FC<DealDetailModalProps> = ({
  deal,
  isOpen,
  onClose,
  progress,
}) => {
  const [comment, setComment] = useState('');
  const { updateTaskStatus, addDealProgress } = useDeals();

  if (!deal) return null;

  // Defensive fallback for task data
  const tasks = (deal as any).tasks || (deal as any).deal_tasks || [];
  const progress_data = (deal as any).progress || (deal as any).deal_progress || [];

  const handleTaskStatusChange = async (task: DealTask, checked: boolean) => {
    try {
      const newStatus = checked ? 'completed' : 'pending';
      const newProgress = checked ? 100 : 0;
      
      await updateTaskStatus(task.id, newStatus, newProgress);
      
      // Add progress entry
      await addDealProgress(
        deal.id,
        `Task "${task.title}" marked as ${newStatus}`,
        `Progress updated to ${newProgress}%`
      );
      
      toast.success(`Task ${checked ? 'completed' : 'reopened'}`);
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    
    try {
      await addDealProgress(deal.id, 'Comment added', comment);
      setComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const getTaskIcon = (status: string) => {
    return status === 'completed' ? (
      <CheckCircle2 className="h-4 w-4 text-green-600" />
    ) : (
      <Circle className="h-4 w-4 text-gray-400" />
    );
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-500/10 text-green-700 border-green-200';
      case 'pending': return 'bg-yellow-500/10 text-yellow-700 border-yellow-200';
      case 'completed': return 'bg-blue-500/10 text-blue-700 border-blue-200';
      case 'cancelled': return 'bg-red-500/10 text-red-700 border-red-200';
      default: return 'bg-gray-500/10 text-gray-700 border-gray-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-start">
            <DialogTitle className="text-xl font-bold">{deal.title}</DialogTitle>
            <Badge variant="secondary" className={getStatusColor(deal.status)}>
              {deal.status}
            </Badge>
          </div>
          {deal.description && (
            <p className="text-muted-foreground">{deal.description}</p>
          )}
        </DialogHeader>

        <div className="space-y-6">
          {/* Deal Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Value</p>
                <p className="font-semibold">${deal.deal_value.toLocaleString()}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="font-semibold capitalize">{deal.deal_type}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Due Date</p>
                <p className="font-semibold">
                  {deal.estimated_completion_date 
                    ? format(new Date(deal.estimated_completion_date), 'MMM dd, yyyy')
                    : 'Not set'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Progress</p>
                <p className="font-semibold">{progress}%</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Overall Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          <Tabs defaultValue="tasks" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="tasks">Tasks</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="messages">
                <MessageCircle className="h-4 w-4 mr-2" />
                Messages
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tasks" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Task List</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {tasks && tasks.length > 0 ? (
                    tasks.map((task) => (
                      <div key={task.id} className="flex items-start gap-3 p-3 border rounded-lg">
                        <Checkbox
                          checked={task.status === 'completed'}
                          onCheckedChange={(checked) => handleTaskStatusChange(task, checked as boolean)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {getTaskIcon(task.status)}
                            <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </h4>
                            <Badge variant="outline">
                              {task.assigned_to}
                            </Badge>
                          </div>
                          {task.description && (
                            <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            {task.due_date && (
                              <span>Due: {format(new Date(task.due_date), 'MMM dd')}</span>
                            )}
                            <span>Progress: {task.progress_percentage}%</span>
                          </div>
                          <Progress value={task.progress_percentage} className="h-1 mt-2" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No tasks available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timeline" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Gantt Chart</CardTitle>
                </CardHeader>
                <CardContent>
                  <ErrorBoundary>
                    <GanttChart tasks={tasks} />
                  </ErrorBoundary>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {progress_data && progress_data.length > 0 ? (
                    progress_data.map((entry) => (
                      <div key={entry.id} className="border-l-2 border-muted pl-4 pb-4">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{entry.status_change}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(entry.created_at), 'MMM dd, HH:mm')}
                          </span>
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No activity yet</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Add Comment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder="Add a comment or update..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                  />
                  <Button onClick={handleAddComment} disabled={!comment.trim()}>
                    Add Comment
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="messages" className="space-y-4">
              <EnhancedDealMessaging dealId={deal.id} />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};